import type {
  InferResponseType,
  MarkConfigOptionalForNonBulkRequests,
  InferFinalResponseType,
  InferConfigType,
  MakeAllKeysRequired,
} from '../types';
import {useGrpcClientConfig} from './GrpcClientContext';
import type {Config as GRPCConfig} from '@aboutyou/grpc-clients/tadarida/aysa_api/shared/v1/config.types';
import {
  GrpcRequestServiceMethod,
  useGenericGrpcRequest,
  GrpcQueryConfigWithSection,
} from './useGenericGrpcRequest';
import {defaultRetry} from '../react-query';
import {DataFetcherRequest} from '../useRequest';
import {isClientTimeoutExceededError} from './code';
import {convertAllNullPropertiesToUndefined} from './convertAllNullPropertiesToUndefined';

type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>;
};

export interface GenericTadaridaConfig {
  config?: DeepPartial<GRPCConfig>;
}

export function useGrpcRequest<
  ServiceMethod extends GrpcRequestServiceMethod,
  MethodName extends ServiceMethod['method'],
  ResponseType extends InferResponseType<ReturnType<ServiceMethod>>,
  ParamsType extends MakeAllKeysRequired<
    Omit<Parameters<ServiceMethod>[1], keyof GenericTadaridaConfig>
  >,
  DefaultConfigType extends GrpcQueryConfigWithSection<
    ResponseType,
    ParamsType
  >,
  GrpcConfigType extends InferConfigType<
    MethodName,
    ResponseType,
    DefaultConfigType
  >,
  TData extends object = InferFinalResponseType<GrpcConfigType, ResponseType>,
>(
  maybeAsyncService: ServiceMethod,
  params: ParamsType,
  ...[grpcQueryConfig]: MarkConfigOptionalForNonBulkRequests<
    MethodName,
    GrpcConfigType
  >
) {
  const genericConfig = useGrpcClientConfig();
  const query = useGenericGrpcRequest(
    maybeAsyncService,
    convertAllNullPropertiesToUndefined<ParamsType>(params),
    {config: genericConfig},
    {retry: grpcDefaultRetry, ...grpcQueryConfig},
  );

  return query as unknown as DataFetcherRequest<TData>;
}

const grpcDefaultRetry = (failureCount: number, error: unknown) => {
  if (!isClientTimeoutExceededError(error)) {
    return false;
  }
  return defaultRetry(failureCount, error);
};
